import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { usePortalContainer } from "@/widgets/core/PortalContainer"

interface SharedRoomModalProps {
  onChoice: (sharedRoom: boolean) => void
  onCancel: () => void
}

export function SharedRoomModal({ onChoice, onCancel }: SharedRoomModalProps) {
  const container = usePortalContainer()
  const overlayRef = useRef<HTMLDivElement>(null)
  const primaryBtnRef = useRef<HTMLButtonElement>(null)
  const secondaryBtnRef = useRef<HTMLButtonElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)

  // Auto-focus primary button
  useEffect(() => {
    primaryBtnRef.current?.focus()

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCancel()
        return
      }
      if (e.key !== "Tab") return

      const focusable = [
        primaryBtnRef.current,
        secondaryBtnRef.current,
        closeBtnRef.current,
      ].filter(Boolean) as HTMLElement[]

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onCancel()
  }

  return createPortal(
    <div
      ref={overlayRef}
      onClick={handleBackdropClick}
      className="@container fixed inset-0 z-[60] flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="shared-room-title"
    >
      <div className="relative w-full max-w-[420px] rounded-2xl border border-border bg-card p-7">
        {/* Close */}
        <Button
          ref={closeBtnRef}
          variant="ghost"
          size="icon"
          onClick={onCancel}
          className="absolute top-4 right-4 size-8 rounded-lg text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="size-4" />
        </Button>

        {/* Icon */}
        <div
          className="mb-4 flex size-11 items-center justify-center rounded-xl"
          style={{
            background: "color-mix(in oklch, var(--primary) 12%, transparent)",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
              fill="var(--primary)"
            />
          </svg>
        </div>

        {/* Title + description */}
        <h2
          id="shared-room-title"
          className="text-base font-bold tracking-wide text-foreground uppercase"
        >
          Open to sharing a room?
        </h2>
        <p className="mt-2 mb-6 text-sm leading-relaxed text-muted-foreground">
          Solo travelers can save by sharing a twin room with another attendee.
          You'll get a lower package price while still staying close to the
          arena.
        </p>

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <Button ref={primaryBtnRef} size="xl" onClick={() => onChoice(false)}>
            No, I want a single room
          </Button>
          <Button
            ref={secondaryBtnRef}
            variant="outline"
            size="xl"
            onClick={() => onChoice(true)}
          >
            Yes, I don't mind a shared room
          </Button>
        </div>
      </div>
    </div>,
    container ?? document.body
  )
}
